import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, API } from '../contexts/AuthContext';
import axios from 'axios';
import logo from '../assets/logo-centrao.png';
import { BASE_ESTUDIO, estaNoEstudio, campanhasRecentes, linkEstudio } from '../utils/estudio';

export default function Sidebar() {
  const { user, logout, temPermissao } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [filiais, setFiliais] = useState([]);

  useEffect(() => { axios.get(`${API}/filiais`).then(res => setFiliais(res.data)).catch(() => {}); }, []);
  const nomeFilial = (codigo) => filiais.find(f => String(f.codigo) === String(codigo))?.nome || `Filial ${codigo}`;

  // Fecha drawer mobile ao navegar
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Fecha drawer ao mudar orientação ou redimensionar
  useEffect(() => {
    function fechar() { setMobileOpen(false); }
    window.addEventListener('orientationchange', fechar);
    window.addEventListener('resize', fechar);
    return () => {
      window.removeEventListener('orientationchange', fechar);
      window.removeEventListener('resize', fechar);
    };
  }, []);

  // Trava scroll do body enquanto drawer aberto
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  const handleLogout = () => { logout(); navigate('/login'); };
  const p = (chave) => temPermissao(chave);

  const temCampanhas = p('campanhas') || p('campanhas_criar') || p('plaquinhas_gerar') || p('campanhas_vendedor') || p('campanhas_vendedor_loja') || p('torcida_ver') || p('torcida_loja') || p('checklist_gerenciar') || p('checklist_loja') || p('vendas_campanhas') || p('premiacoes') || p('premiacoes_loja');
  const temLand = p('land_ver') || p('land_gerenciar');
  const temParceiros = p('parceiros_ver') || p('parceiros_gerenciar');
  const temAdmin = p('usuarios') || p('perfis') || p('log_atividades');
  // Estúdio de Criação: mundo à parte (utils/estudio.js). Dentro dele o menu é outro.
  const temEstudio = p('campanhas_editar');
  const noEstudio = temEstudio && estaNoEstudio(location.pathname);
  const [recentes, setRecentes] = useState(campanhasRecentes);
  useEffect(() => {
    const atualizar = () => setRecentes(campanhasRecentes());
    window.addEventListener('estudio-recentes', atualizar);
    return () => window.removeEventListener('estudio-recentes', atualizar);
  }, []);

  return (
    <>
      {/* Header mobile com hamburger */}
      <div className="mobile-header">
        <button onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? 'Fechar menu' : 'Abrir menu'}>
          <i className={`bi ${mobileOpen ? 'bi-x-lg' : 'bi-list'}`}></i>
        </button>
        <img src={logo} alt="Centrão" className="sidebar-logo" />
        <div className="sidebar-title">
          Marketing
          <span>Centrão</span>
        </div>
      </div>

      {/* Overlay escuro */}
      <div className={`sidebar-overlay ${mobileOpen ? 'open' : ''}`} onClick={() => setMobileOpen(false)} />

      {/* Sidebar — rail (colapsada por padrão, expande no hover no desktop; drawer no mobile) */}
      <nav className={`sidebar ${mobileOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <img src={logo} alt="Centrão" className="sidebar-logo" />
          <div className="sidebar-title sidebar-label">
            {noEstudio ? 'Estúdio de Criação' : 'Marketing'}
            <span>{noEstudio ? 'Marketing Centrão' : 'Centrão'}</span>
          </div>
        </div>

        {noEstudio ? (
          <div className="sidebar-nav">
            <NavLink to="/campanhas" className="sidebar-voltar" title="Voltar ao Marketing">
              <i className="bi bi-arrow-left"></i>
              <span className="nav-label sidebar-label">Voltar ao Marketing</span>
            </NavLink>
            <div className="sidebar-section sidebar-label">Estúdio de Criação</div>
            <NavLink to={BASE_ESTUDIO} end title="Campanhas do estúdio">
              <i className="bi bi-grid-3x3-gap"></i>
              <span className="nav-label sidebar-label">Campanhas</span>
            </NavLink>
            {recentes.length > 0 && <div className="sidebar-section sidebar-label">Abertas por último</div>}
            {recentes.map(c => (
              <NavLink key={c.id} to={linkEstudio(c.id)} title={c.nome}
                className={() => (location.pathname === `${BASE_ESTUDIO}/${c.id}` ? 'active' : '')}>
                <i className="bi bi-brush"></i>
                <span className="nav-label sidebar-label" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.nome}</span>
              </NavLink>
            ))}
          </div>
        ) : (
        <div className="sidebar-nav">
          {temEstudio && (
            <NavLink to={BASE_ESTUDIO} className="sidebar-estudio" title="Estúdio de Criação — stories, pôster e catálogo">
              <i className="bi bi-palette-fill"></i>
              <span className="nav-label sidebar-label">Estúdio de Criação</span>
            </NavLink>
          )}
          <div className="sidebar-section sidebar-label">Geral</div>
          {p('dashboard') && (
            <NavLink to="/dashboard" title="Dashboard">
              <i className="bi bi-speedometer2"></i>
              <span className="nav-label sidebar-label">Dashboard</span>
            </NavLink>
          )}
          {p('aniversariantes') && (
            <NavLink to="/aniversariantes" title="Aniversariantes">
              <i className="bi bi-cake2"></i>
              <span className="nav-label sidebar-label">Aniversariantes</span>
            </NavLink>
          )}
          {p('analises_venda') && (
            <NavLink to="/analises-venda" title="Análises de Venda">
              <i className="bi bi-graph-up-arrow"></i>
              <span className="nav-label sidebar-label">Análises de Venda</span>
            </NavLink>
          )}
          {p('monitor_ramais') && (
            <NavLink to="/monitor-ramais" title="Monitor de Ramais">
              <i className="bi bi-telephone-fill"></i>
              <span className="nav-label sidebar-label">Monitor de Ramais</span>
            </NavLink>
          )}

          {p('demandas') && (
            <>
              <div className="sidebar-section sidebar-label">Demandas</div>
              <NavLink to="/demandas" title="Demandas do Marketing"><i className="bi bi-kanban"></i><span className="nav-label sidebar-label">Demandas</span></NavLink>
            </>
          )}

          {temCampanhas && (
            <>
              <div className="sidebar-section sidebar-label">Campanhas</div>
              {p('campanhas') && <NavLink to="/campanhas" title="Campanhas"><i className="bi bi-megaphone"></i><span className="nav-label sidebar-label">Campanhas</span></NavLink>}
              {(p('campanhas_vendedor') || p('campanhas_vendedor_loja')) && <NavLink to="/campanhas-vendedor" title="Camp. Premiação"><i className="bi bi-trophy"></i><span className="nav-label sidebar-label">Camp. Premiação</span></NavLink>}
              {p('vendas_campanhas') && <NavLink to="/vendas-campanhas" title="Vendas Campanhas"><i className="bi bi-graph-up"></i><span className="nav-label sidebar-label">Vendas Campanhas</span></NavLink>}
              {(p('premiacoes') || p('premiacoes_loja')) && <NavLink to="/premiacoes" title="Premiações"><i className="bi bi-cash-coin"></i><span className="nav-label sidebar-label">Premiações</span></NavLink>}
              {p('plaquinhas_gerar') && <NavLink to="/plaquinhas" title="Plaquinhas"><i className="bi bi-printer"></i><span className="nav-label sidebar-label">Plaquinhas</span></NavLink>}
              {(p('torcida_ver') || p('torcida_loja')) && <NavLink to="/torcida/admin" title="Torcida Centrão"><i className="bi bi-flag-fill"></i><span className="nav-label sidebar-label">Torcida Centrão</span></NavLink>}
              {(p('checklist_gerenciar') || p('checklist_loja')) && <NavLink to="/checklist" title="Checklist de Campanha"><i className="bi bi-check2-square"></i><span className="nav-label sidebar-label">Checklist</span></NavLink>}
            </>
          )}

          {temLand && (
            <>
              <div className="sidebar-section sidebar-label">Land</div>
              <NavLink to="/land" title="Land"><i className="bi bi-window-stack"></i><span className="nav-label sidebar-label">Land</span></NavLink>
            </>
          )}

          {temParceiros && (
            <>
              <div className="sidebar-section sidebar-label">Parceiros</div>
              <NavLink to="/parceiros" title="Parceiros"><i className="bi bi-people-fill"></i><span className="nav-label sidebar-label">Parceiros</span></NavLink>
            </>
          )}

          {temAdmin && (
            <>
              <div className="sidebar-section sidebar-label">Administração</div>
              {p('usuarios') && <NavLink to="/usuarios" title="Usuários"><i className="bi bi-person-gear"></i><span className="nav-label sidebar-label">Usuários</span></NavLink>}
              {p('perfis') && <NavLink to="/perfis" title="Perfis"><i className="bi bi-shield-lock"></i><span className="nav-label sidebar-label">Perfis</span></NavLink>}
              {p('log_atividades') && <NavLink to="/log-atividades" title="Log de Atividades"><i className="bi bi-journal-text"></i><span className="nav-label sidebar-label">Log de Atividades</span></NavLink>}
            </>
          )}
        </div>
        )}

        {/* Bloco inferior: usuário + ações */}
        <div className="sidebar-bottom">
          <div className="sidebar-user-info sidebar-label">
            <i className="bi bi-person-circle me-2"></i>{user?.nome}
            <div className="sidebar-user-grupo">
              {user?.perfil_nome}{user?.filial_id ? ` — ${nomeFilial(user.filial_id)}` : ''}
            </div>
          </div>
          <div className="sidebar-acoes-row">
            <NavLink to="/meu-perfil" className="sidebar-acao-btn" title="Meu Perfil">
              <i className="bi bi-gear"></i>
              <span className="sidebar-label">Perfil</span>
            </NavLink>
            <button className="sidebar-acao-btn" onClick={handleLogout} title="Sair">
              <i className="bi bi-box-arrow-left"></i>
              <span className="sidebar-label">Sair</span>
            </button>
          </div>
        </div>

        <div className="sidebar-footer sidebar-label">
          Elaborado por: Lucas Xavier Fernandes
        </div>
      </nav>
    </>
  );
}
